import { Controller } from "@hotwired/stimulus"

// Cloudflare input IDs are stable; broadcast video IDs are not. Follow lifecycle
// independently of agenda timing and let Vidstack/hls.js handle transient errors.
export default class extends Controller {
  static targets = ["mount", "slate", "status", "notice", "template"]
  static values = { baseUrl: String, inputs: Array }

  connect() {
    this.stopped = false
    this.videoId = null
    this.inputId = null
    this.retryPlayback = false
    this.failures = 0
    this.dispatch("state", { detail: { offline: false } })
    this.refresh()
  }

  disconnect() {
    this.stop()
  }

  stop() {
    this.stopped = true
    clearTimeout(this.timer)
    clearTimeout(this.requestTimeout)
    this.request?.abort()
    this.request = null
    this.clearPlayer()
  }

  inputAt(now = Date.now()) {
    return this.inputsValue.find(input => !input.startsAt ||
      (Date.parse(input.startsAt) <= now && now < Date.parse(input.endsAt)))
  }

  async refresh() {
    if (this.stopped || this.request) return
    clearTimeout(this.timer)
    const input = this.inputAt()
    if (input?.id !== this.inputId) {
      this.clearPlayer()
      this.inputId = input?.id
      this.failures = 0
      this.noticeTarget.hidden = true
      this.dispatch("state", { detail: { offline: false } })
    }
    if (!input) {
      this.dispatch("state", { detail: { offline: true } })
      this.statusTarget.textContent = "The main-stage broadcast is offline. Join us September 24–25, Pacific time."
      this.schedule()
      return
    }

    const request = new AbortController()
    this.request = request
    this.requestTimeout = setTimeout(() => request.abort(), 8_000)
    try {
      const response = await fetch(`${this.baseUrlValue}/${input.id}/lifecycle`, {
        signal: request.signal, cache: "no-store", credentials: "omit"
      })
      if (!response.ok) throw new Error(`Cloudflare status: HTTP ${response.status}`)
      const state = await response.json()
      if (state.isInput !== true || typeof state.live !== "boolean" ||
          (state.live && !/^[a-f0-9]{32}$/.test(state.videoUID))) {
        throw new Error("Invalid Cloudflare lifecycle response")
      }
      // Ignore a late response after navigation or after crossing a day boundary.
      if (this.stopped || this.request !== request || this.inputAt()?.id !== input.id) return
      this.failures = 0
      this.noticeTarget.hidden = true
      if (state.live) {
        if (state.videoUID !== this.videoId || this.retryPlayback) this.showPlayer(state.videoUID)
      } else {
        this.clearPlayer()
        this.statusTarget.textContent = "Stay tuned. Playback will start automatically when the stream is available."
      }
      this.dispatch("state", { detail: { offline: !state.live } })
    } catch (error) {
      if (this.stopped || this.request !== request) return
      this.failures++
      // Keep an existing player running through a status-service failure.
      // this.noticeTarget.textContent = "Unable to check live status. Retrying automatically…"
      // this.noticeTarget.hidden = false
    } finally {
      if (this.request === request) {
        clearTimeout(this.requestTimeout)
        this.request = null
        this.schedule()
      }
    }
  }

  schedule() {
    if (this.stopped) return
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.refresh(), Math.min(60_000, 10_000 * 2 ** this.failures))
  }

  playbackFailed() {
    // Fatal player errors only. Recreate on the next status check rather than
    // spinning an immediate retry loop; nonfatal HLS retries remain in hls.js.
    this.retryPlayback = true
    // this.noticeTarget.textContent = "Playback interrupted. Reconnecting automatically…"
    // this.noticeTarget.hidden = false
  }

  showPlayer(videoId) {
    const fragment = this.templateTarget.content.cloneNode(true)
    const player = fragment.querySelector("media-player")
    const source = `${this.baseUrlValue}/${videoId}/manifest/video.m3u8`
    player.setAttribute("src", source)
    player.setAttribute("data-video-player-source-value", source)
    this.mountTarget.replaceChildren(fragment)
    this.slateTarget.hidden = true
    this.videoId = videoId
    this.retryPlayback = false
  }

  clearPlayer() {
    this.mountTarget.replaceChildren()
    this.slateTarget.hidden = false
    this.videoId = null
    this.retryPlayback = false
  }
}
